// Wire model for GET /mapTilingService/prices -> { prices: [...] }.
//
// Replaces the old priceTileJsonV2 vector-tile source. Plain objects rather than classes: these
// values go into a mapbox-gl geojson source and come back out through queryRenderedFeatures(), so
// they have to stay serialisable.

export interface MillPriceTier {
  name: string;
  delta: number;
  price: number;
  cardinality: number;
  price_pempem: number;
}

export interface MillPriceOnMap {
  id: number;
  mill_id: number;
  mill_name?: string;
  mill_alias?: string;
  mill_full_name?: string;
  group_id?: number;
  off_platform: boolean;

  coordinates?: number[];
  location_village?: string;
  location_subdistrict?: string;

  market_date?: string;
  price_type?: string;
  program_name?: string;

  price_per_kg?: number;
  program_delta_bonus?: number;
  tiers: MillPriceTier[];
  // Not sent by the API. Synthesised so the bubble layer's existing text-field expression
  // (['get','price_count']) keeps rendering "N prices" for quality-based mills.
  price_count: number;

  minimum_quality_level?: string;
  sorting_strictness?: string;
  minimum_bjr?: number;
  standard_grading_ratio?: number;
  require_spb?: string;
  require_spsi: boolean;
  accepts_small_truck: boolean;
  accepts_dropoff: boolean;
  is_brondolan: boolean;
  unloading_fee_pre_applied: boolean;
  bid_warning_message?: string;

  mill_officer_name?: string;
  mill_officer_phonenumber?: string;
}

export const buildMillPrice = (p: any): MillPriceOnMap => {
  const tiers = buildTiers(p.pricing?.tiers);

  return {
    id: p.id,
    mill_id: p.mill_id,
    mill_name: p.mill_name,
    mill_alias: p.mill_alias,
    mill_full_name: p.mill_full_name,
    group_id: p.group_id,
    off_platform: p.off_platform ?? false,

    coordinates: parseCoordinates(p.position),
    location_village: p.location_village,
    location_subdistrict: p.location_subdistrict,

    market_date: p.market_date,
    price_type: p.price_type,
    program_name: p.program_name,

    price_per_kg: p.pricing?.price_per_kg,
    program_delta_bonus: p.pricing?.program_delta_bonus,
    tiers,
    price_count: tiers.length,

    minimum_quality_level: p.minimum_quality_level,
    sorting_strictness: p.sorting_strictness,
    minimum_bjr: p.minimum_bjr,
    standard_grading_ratio: p.standard_grading_ratio,
    require_spb: p.require_spb,
    require_spsi: p.require_spsi ?? false,
    accepts_small_truck: p.accepts_small_truck ?? false,
    accepts_dropoff: p.accepts_dropoff ?? false,
    is_brondolan: p.is_brondolan ?? false,
    unloading_fee_pre_applied: p.unloading_fee_pre_applied ?? false,
    bid_warning_message: p.bid_warning_message,

    mill_officer_name: p.mill_officer_name,
    mill_officer_phonenumber: p.mill_officer_phonenumber,
  };
};

// Off-platform mills cannot take a NAM contract, and a price with no position cannot be mapped.
export const buildMillPrices = (prices: any[]): MillPriceOnMap[] =>
  prices?.map((p) => buildMillPrice(p)).filter((p) => p.coordinates && !p.off_platform) ?? [];

// Best tier first, so the tier list reads high-to-low wherever it is displayed.
const buildTiers = (tiers: any[]): MillPriceTier[] =>
  (tiers?.map((t: any) => ({
    name: t.name,
    delta: t.delta,
    price: t.price,
    cardinality: t.cardinality,
    price_pempem: t.price_pempem,
  })) ?? []).sort((a, b) => (b.cardinality ?? 0) - (a.cardinality ?? 0));

// `position` arrives as a stringified GeoJSON Point - one malformed row must not lose the payload.
const parseCoordinates = (position: any): number[] | undefined => {
  if (!position) return undefined;
  try {
    const parsed = typeof position === 'string' ? JSON.parse(position) : position;
    return Array.isArray(parsed?.coordinates) ? parsed.coordinates : undefined;
  } catch (e) {
    console.error('Error parsing price.position:', e);
    return undefined;
  }
};

export const toPriceFeatureCollection = (prices: MillPriceOnMap[]): GeoJSON.FeatureCollection => ({
  type: 'FeatureCollection',
  features: prices.map((price) => ({
    type: 'Feature' as const,
    geometry: {
      type: 'Point' as const,
      coordinates: price.coordinates as number[],
    },
    properties: { ...price },
  })),
});
